import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { wikiAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import WikiEditor from '../components/WikiEditor';
import WikiImage from '../components/WikiImage';
import { ChevronRight, ChevronDown, Plus, Edit, Trash2, Folder, FileText, BookOpen } from 'lucide-react';

const Wiki = () => {
  const { showSuccess, showError } = useNotification();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [editingPage, setEditingPage] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  
  // Create modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('page'); // 'section' | 'page'
  const [createParentId, setCreateParentId] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');

  useEffect(() => {
    loadTree();
  }, []);

  const loadTree = async () => {
    try {
      setLoading(true);
      const res = await wikiAPI.getTree('project');
      setTree(res.data.tree || []);
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось загрузить Wiki');
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (pageId) => {
    try {
      const res = await wikiAPI.getPage(pageId);
      setSelectedPage(res.data);
      setEditingPage(null);
      setEditContent('');
      setEditTitle('');
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось загрузить страницу');
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) {
      showError('Нужно указать заголовок');
      return;
    }

    try {
      const data = {
        title: newTitle.trim(),
        body_md: newContent.trim(),
        parent_id: createParentId,
        order: 0
      };

      if (createType === 'section') {
        await wikiAPI.createSection(data);
      } else {
        await wikiAPI.createPage(data);
      }

      showSuccess(`${createType === 'section' ? 'Раздел' : 'Страница'} создан`);
      setShowCreateModal(false);
      setNewTitle('');
      setNewContent('');
      setCreateParentId(null);
      loadTree();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось создать');
    }
  };

  const handleStartEdit = () => {
    if (!selectedPage) return;
    setEditingPage(selectedPage._id);
    setEditTitle(selectedPage.title || '');
    setEditContent(selectedPage.body_md || '');
  };

  const handleSaveEdit = async () => {
    if (!editingPage) return;
    if (!editTitle.trim()) {
      showError('Нужно указать заголовок');
      return;
    }

    try {
      await wikiAPI.updatePage(editingPage, {
        title: editTitle.trim(),
        body_md: editContent.trim()
      });
      showSuccess('Страница обновлена');
      setEditingPage(null);
      loadPage(editingPage);
      loadTree();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось обновить');
    }
  };

  const handleCancelEdit = () => {
    setEditingPage(null);
    setEditTitle('');
    setEditContent('');
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Удалить эту страницу/раздел? Это действие необратимо.')) return;
    try {
      await wikiAPI.deletePage(pageId);
      showSuccess('Удалено');
      if (selectedPage && selectedPage._id === pageId) {
        setSelectedPage(null);
      }
      loadTree();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось удалить');
    }
  };

  const openCreateModal = (type, parentId = null) => {
    setCreateType(type);
    setCreateParentId(parentId);
    setNewTitle('');
    setNewContent('');
    setShowCreateModal(true);
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node._id);
    const isSelected = selectedPage && selectedPage._id === node._id;
    const isSection = node.type === 'section';

    return (
      <div key={node._id}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            paddingLeft: `${12 + level * 20}px`,
            cursor: 'pointer',
            backgroundColor: isSelected ? 'var(--bg-secondary)' : 'transparent',
            borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
            hover: { backgroundColor: 'var(--bg-secondary)' }
          }}
          onClick={() => {
            if (hasChildren) {
              toggleNode(node._id);
            }
            loadPage(node._id);
          }}
          onMouseEnter={(e) => {
            if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
          }}
          onMouseLeave={(e) => {
            if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            {hasChildren ? (
              <div onClick={(e) => { e.stopPropagation(); toggleNode(node._id); }}>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            ) : (
              <div style={{ width: '16px' }} />
            )}
            {isSection ? <Folder size={16} /> : <FileText size={16} />}
            <span style={{ fontWeight: isSection ? '600' : '400' }}>
              {node.title || 'Без названия'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
            <button
              className="btn"
              style={{ padding: '4px 8px', fontSize: '12px' }}
              onClick={() => openCreateModal(isSection ? 'page' : 'section', node._id)}
              title={isSection ? 'Добавить страницу' : 'Добавить подраздел'}
            >
              <Plus size={14} />
            </button>
            <button
              className="btn btn-danger"
              style={{ padding: '4px 8px', fontSize: '12px' }}
              onClick={() => handleDelete(node._id)}
              title="Удалить"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 0px)', overflow: 'hidden' }}>
      {/* Left sidebar - Tree */}
      <div style={{
        width: '300px',
        borderRight: '1px solid var(--border-color)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--bg-primary)'
      }}>
        <div style={{
          padding: '16px',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BookOpen size={20} />
            Wiki
          </h2>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              className="btn btn-primary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
              onClick={() => openCreateModal('section', null)}
              title="Создать раздел"
            >
              <Plus size={14} />
            </button>
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
              onClick={() => openCreateModal('page', null)}
              title="Создать страницу"
            >
              <FileText size={14} />
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Загрузка...
            </div>
          ) : tree.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Страниц пока нет. Создайте раздел или страницу.
            </div>
          ) : (
            tree.map(node => renderTreeNode(node))
          )}
        </div>
      </div>

      {/* Right panel - Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedPage ? (
          <>
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h1 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {editingPage === selectedPage._id ? (
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="form-input"
                    style={{ fontSize: '20px', fontWeight: '600', width: '100%', maxWidth: '600px' }}
                    placeholder="Заголовок страницы"
                  />
                ) : (
                  selectedPage.title || 'Без названия'
                )}
              </h1>
              {editingPage !== selectedPage._id && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary" onClick={handleStartEdit}>
                    <Edit size={16} style={{ marginRight: '4px' }} />
                    Изменить
                  </button>
                  <button className="btn btn-danger" onClick={() => handleDelete(selectedPage._id)}>
                    <Trash2 size={16} style={{ marginRight: '4px' }} />
                    Удалить
                  </button>
                </div>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
              {editingPage === selectedPage._id ? (
                <WikiEditor
                  content={editContent}
                  onChange={setEditContent}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                  isEditing={true}
                />
              ) : (
                <div style={{ color: 'var(--text-primary)' }}>
                  <ReactMarkdown
                    components={{
                      img: ({ node, ...props }) => {
                        return <WikiImage {...props} />;
                      }
                    }}
                  >
                    {selectedPage.body_md || '*Нет содержимого*'}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-secondary)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <BookOpen size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>Выберите страницу в боковой панели для просмотра или редактирования</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '600px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto' }}>
            <h2 style={{ fontSize: '18px', marginBottom: '16px' }}>
              Создать {createType === 'section' ? 'раздел' : 'страницу'}
            </h2>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Заголовок *
              </label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="form-input"
                placeholder="Введите заголовок"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Содержимое {createType === 'section' ? '(необязательно)' : ''}
              </label>
              <WikiEditor
                content={newContent}
                onChange={setNewContent}
                onSave={null}
                onCancel={null}
                isEditing={false}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                Отмена
              </button>
              <button className="btn btn-primary" onClick={handleCreate}>
                Создать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wiki;
